<script>
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

export default {
  name: 'FollowUpModal',
  components: {
    NextButton,
    Spinner,
    WhatsAppTemplateParser,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return {
      form: {
        title: '',
        message: '',
        inboxId: this.inboxId,
        scheduledAt: '',
      },
      messageMode: 'custom', // 'custom' | 'template' (internal) | 'whatsapp_template' (Meta)
      selectedTemplateId: null,
      // Meta-approved template selected when the inbox is WhatsApp Cloud.
      selectedWhatsappTemplate: null,
      // Deep validity of the Meta template variables, kept in sync via the
      // parser's @validity-change event ($refs are not reactive in Options API).
      whatsappTemplateValid: false,
      isEditing: false,
      editingMessageId: null,
      isSubmitting: false,
      isLoadingMessages: false,
      isDeleting: false,
      showDeleteConfirm: false,
      messageToDelete: null,
    };
  },
  computed: {
    localShow: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      },
    },
    availableInboxes() {
      return this.$store.getters['inboxes/getInboxes'] || [];
    },
    selectedInbox() {
      return (
        this.availableInboxes.find(i => i.id === this.form.inboxId) || null
      );
    },
    // Meta templates only exist on the official WhatsApp Cloud API. WAHA/UazAPI
    // (Channel::Api) never see the Meta-template tab.
    isCloudInbox() {
      const inbox = this.selectedInbox;
      return (
        !!inbox &&
        inbox.channel_type === 'Channel::Whatsapp' &&
        inbox.provider === 'whatsapp_cloud'
      );
    },
    whatsappTemplates() {
      return (
        this.$store.getters['inboxes/getFilteredWhatsAppTemplates'](
          this.form.inboxId
        ) || []
      );
    },
    templates() {
      return this.$store.getters['followUpTemplates/getActiveTemplates'] || [];
    },
    selectedTemplate() {
      if (!this.selectedTemplateId) return null;
      return this.templates.find(t => t.id === this.selectedTemplateId);
    },
    followUps() {
      return (
        this.$store.getters['followUps/getFollowUps'](this.conversationId) || []
      );
    },
    sortedMessages() {
      return [...this.followUps].sort(
        (a, b) => a.scheduled_at - b.scheduled_at
      );
    },
    minDateTime() {
      const now = new Date();
      now.setMinutes(now.getMinutes() + 1);
      // Use local time for the min attribute so the picker boundary reflects the
      // user's timezone rather than UTC.
      return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
    },
    isFormValid() {
      const scheduleValid =
        this.form.inboxId &&
        this.form.scheduledAt &&
        new Date(this.form.scheduledAt) > new Date();
      if (!scheduleValid) return false;

      // Meta template mode: the message is the template itself — the agent is NOT
      // required to type free text. Require a selected template with all its
      // variables filled (whatsappTemplateValid is fed by the parser's event).
      if (this.messageMode === 'whatsapp_template') {
        return (
          Boolean(this.selectedWhatsappTemplate) && this.whatsappTemplateValid
        );
      }

      // Mesma isenção para MODELO MULTI-ITEM nosso: a mensagem mora nos itens e
      // `content` fica em branco de propósito, então applyTemplate() não tem o que
      // copiar e o campo segue vazio. Sem isto o agendamento fica travado com o
      // modelo já selecionado e "Modelo aplicado" na tela — o agente é obrigado a
      // digitar um texto que o backend descarta, porque o disparo usa os itens.
      // Espelha exatamente FollowUp#content_optional? (uses_items_template? ||
      // ai_generated?), corrigido no backend em 2026-05-30 (RENDER-03) e nunca
      // refletido aqui: automação e pipeline passaram a funcionar, o modal manual não.
      if (
        this.messageMode === 'template' &&
        this.selectedTemplate?.uses_items
      ) {
        return Boolean(this.selectedTemplateId);
      }

      return Boolean(this.form.message.trim());
    },
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.fetchFollowUps();
        this.fetchTemplates();
        this.form.inboxId = this.inboxId;
      }
    },
    selectedTemplateId(newId) {
      if (newId && this.messageMode === 'template') {
        this.applyTemplate();
      }
    },
    // If the chosen inbox stops being WhatsApp Cloud, the Meta-template mode is no
    // longer valid — fall back to a custom message.
    isCloudInbox(isCloud) {
      if (!isCloud && this.messageMode === 'whatsapp_template') {
        this.setMessageMode('custom');
      }
    },
  },
  methods: {
    async fetchFollowUps() {
      this.isLoadingMessages = true;
      try {
        await this.$store.dispatch('followUps/fetch', this.conversationId);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching follow-ups:', error);
      } finally {
        this.isLoadingMessages = false;
      }
    },
    async fetchTemplates() {
      try {
        await this.$store.dispatch('followUpTemplates/fetch');
      } catch {
        // Templates may already be loaded
      }
    },
    applyTemplate() {
      const template = this.selectedTemplate;
      if (!template) return;

      // Get context for variable substitution
      const context = this.getTemplateContext();

      // Render template with variables
      let content = template.content;
      Object.keys(context).forEach(key => {
        const placeholder = `{{${key}}}`;
        content = content.replace(new RegExp(placeholder, 'g'), context[key]);
      });

      // Apply to form
      this.form.message = content;
      if (!this.form.title) {
        this.form.title = template.name;
      }
    },
    getTemplateContext() {
      // Get conversation data for variable substitution
      const conversation = this.$store.getters.getSelectedChat;
      const contact = conversation?.meta?.sender || {};
      const inbox = this.availableInboxes.find(i => i.id === this.form.inboxId);
      const currentUser = this.$store.getters.getCurrentUser;

      return {
        contact_name: contact.name || '',
        contact_phone: contact.phone_number || '',
        contact_email: contact.email || '',
        conversation_id: this.conversationId?.toString() || '',
        inbox_name: inbox?.name || '',
        agent_name: currentUser?.name || '',
        current_date: new Date().toLocaleDateString('pt-BR'),
        current_time: new Date().toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
        }),
      };
    },
    setMessageMode(mode) {
      this.messageMode = mode;
      if (mode !== 'template') this.selectedTemplateId = null;
      if (mode !== 'whatsapp_template') {
        this.selectedWhatsappTemplate = null;
        this.whatsappTemplateValid = false;
      }
    },
    onSelectWhatsappTemplate(event) {
      const name = event?.target?.value ?? event;
      // Reset validity until the newly-mounted parser re-emits for this template.
      this.whatsappTemplateValid = false;
      this.selectedWhatsappTemplate =
        this.whatsappTemplates.find(t => t.name === name) || null;
    },
    onTemplateValidity(valid) {
      this.whatsappTemplateValid = valid;
    },
    buildCreatePayload(scheduledAt) {
      // Meta template mode: the follow-up carries the approved template; the
      // rendered body becomes the stored content (shown in the timeline) and the
      // backend delivers it via SendOnWhatsappService as a template.
      if (
        this.messageMode === 'whatsapp_template' &&
        this.selectedWhatsappTemplate
      ) {
        const parser = this.$refs.templateParser;
        const tpl = this.selectedWhatsappTemplate;
        return {
          content: parser?.renderedTemplate || tpl.name,
          scheduledAt,
          title: this.form.title || tpl.name,
          inboxId: this.form.inboxId,
          templateParams: {
            name: tpl.name,
            category: tpl.category,
            language: tpl.language,
            namespace: tpl.namespace,
            processed_params: parser?.processedParams || {},
          },
        };
      }

      const messageData = {
        content: this.form.message,
        scheduledAt,
        title: this.form.title,
        inboxId: this.form.inboxId,
      };
      // Internal follow-up template (for attachments support)
      if (this.messageMode === 'template' && this.selectedTemplateId) {
        messageData.templateId = this.selectedTemplateId;
      }
      return messageData;
    },
    async onSubmit() {
      if (!this.isFormValid) return;

      this.isSubmitting = true;
      try {
        const scheduledAt = new Date(this.form.scheduledAt).toISOString();

        if (this.isEditing) {
          await this.$store.dispatch('followUps/update', {
            conversationId: this.conversationId,
            id: this.editingMessageId,
            messageData: {
              content: this.form.message,
              scheduledAt,
              title: this.form.title,
              inboxId: this.form.inboxId,
            },
          });
          useAlert(this.$t('FOLLOW_UPS.MESSAGES.UPDATE_SUCCESS'));
          this.cancelEdit();
        } else {
          const messageData = this.buildCreatePayload(scheduledAt);
          await this.$store.dispatch('followUps/create', {
            conversationId: this.conversationId,
            messageData,
          });
          useAlert(this.$t('FOLLOW_UPS.MESSAGES.CREATE_SUCCESS'));
          this.resetForm();
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.message ||
          this.$t('FOLLOW_UPS.MESSAGES.ERROR');
        useAlert(errorMessage);
        // eslint-disable-next-line no-console
        console.error('Follow-up error:', error?.response?.data || error);
      } finally {
        this.isSubmitting = false;
      }
    },
    editMessage(message) {
      this.isEditing = true;
      this.editingMessageId = message.id;
      const date = new Date(message.scheduled_at * 1000);
      // Convert UTC timestamp to local datetime string for datetime-local input.
      // Subtracting the timezone offset expresses the UTC instant using local-time digits,
      // so the input shows the correct local time (not the UTC equivalent).
      const localDateTime = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 16);
      this.form = {
        title: message.title || '',
        message: message.message,
        inboxId: message.inbox_id,
        scheduledAt: localDateTime,
      };
      // Editing is text-only — never carry a Meta-template selection into an edit
      // (the backend clears stale template_params when content is rewritten).
      this.messageMode = 'custom';
      this.selectedWhatsappTemplate = null;
      this.whatsappTemplateValid = false;
    },
    cancelEdit() {
      this.isEditing = false;
      this.editingMessageId = null;
      this.resetForm();
    },
    confirmDelete(message) {
      this.messageToDelete = message;
      this.showDeleteConfirm = true;
    },
    async deleteMessage() {
      if (!this.messageToDelete) return;

      this.isDeleting = true;
      try {
        await this.$store.dispatch('followUps/delete', {
          conversationId: this.conversationId,
          id: this.messageToDelete.id,
        });
        useAlert(this.$t('FOLLOW_UPS.MESSAGES.DELETE_SUCCESS'));
        this.showDeleteConfirm = false;
        this.messageToDelete = null;
      } catch (error) {
        // BUG-03 (audit 2026-06-16): the backend only allows deleting PENDING
        // follow-ups. If one transitioned to sent/failed/cancelled (e.g. an
        // overdue one the worker just processed), surface a clear reason instead
        // of a generic error, and close the now-stale confirm dialog.
        useAlert(
          error?.response?.status === 422
            ? this.$t('FOLLOW_UPS.MESSAGES.DELETE_NOT_PENDING')
            : this.$t('FOLLOW_UPS.MESSAGES.ERROR')
        );
        this.showDeleteConfirm = false;
        this.messageToDelete = null;
      } finally {
        this.isDeleting = false;
      }
    },
    resetForm() {
      this.form = {
        title: '',
        message: '',
        inboxId: this.inboxId,
        scheduledAt: '',
      };
      this.messageMode = 'custom';
      this.selectedTemplateId = null;
      this.selectedWhatsappTemplate = null;
      this.whatsappTemplateValid = false;
    },
    formatDate(timestamp) {
      const date = new Date(timestamp * 1000);
      return new Intl.DateTimeFormat('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(date);
    },
    getStatusClass(status) {
      switch (status) {
        case 'pending':
          return 'bg-n-amber-3 text-n-amber-11';
        case 'sent':
          return 'bg-n-teal-3 text-n-teal-11';
        case 'failed':
          return 'bg-n-ruby-3 text-n-ruby-11';
        default:
          return 'bg-n-slate-3 text-n-slate-11';
      }
    },
    getStatusLabel(status) {
      return this.$t(`FOLLOW_UPS.STATUS.${status.toUpperCase()}`);
    },
    onClose() {
      this.resetForm();
      this.cancelEdit();
      this.$emit('close');
    },
  },
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    size="modal-big"
    class="follow-up-modal"
    @close="onClose"
  >
    <woot-modal-header
      :header-title="$t('FOLLOW_UPS.MODAL.TITLE')"
      :header-content="$t('FOLLOW_UPS.MODAL.DESCRIPTION')"
    />
    <div class="p-6 max-h-[75vh] overflow-y-auto">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        <!-- Left Column: Schedule Form -->
        <div class="flex flex-col gap-4 lg:pr-4">
          <h4
            class="text-sm font-semibold text-n-slate-12 pb-2 border-b border-n-strong"
          >
            {{
              isEditing
                ? $t('FOLLOW_UPS.FORM.EDIT_TITLE')
                : $t('FOLLOW_UPS.FORM.NEW_TITLE')
            }}
          </h4>

          <!-- Message Mode Tabs -->
          <div class="flex gap-1 p-1 bg-n-alpha-2 rounded-lg">
            <button
              type="button"
              class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all"
              :class="[
                messageMode === 'custom'
                  ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="setMessageMode('custom')"
            >
              {{ $t('FOLLOW_UPS.FORM.CUSTOM_MESSAGE') }}
            </button>
            <button
              type="button"
              class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all"
              :class="[
                messageMode === 'template'
                  ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="setMessageMode('template')"
            >
              {{ $t('FOLLOW_UPS.FORM.USE_TEMPLATE') }}
            </button>
            <button
              v-if="isCloudInbox && !isEditing"
              type="button"
              class="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all"
              :class="[
                messageMode === 'whatsapp_template'
                  ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12',
              ]"
              @click="setMessageMode('whatsapp_template')"
            >
              {{ $t('FOLLOW_UPS.FORM.USE_WHATSAPP_TEMPLATE') }}
            </button>
          </div>

          <!-- WhatsApp Meta template selector (Cloud inbox only) -->
          <div
            v-if="messageMode === 'whatsapp_template'"
            class="flex flex-col gap-1.5"
          >
            <label class="text-sm font-medium text-n-slate-11">
              {{ $t('FOLLOW_UPS.FORM.SELECT_WHATSAPP_TEMPLATE') }}
              <span class="text-n-ruby-10">*</span>
            </label>
            <div
              v-if="whatsappTemplates.length === 0"
              class="p-3 bg-n-amber-3 border border-n-amber rounded-lg"
            >
              <p class="text-sm text-n-amber-11">
                {{ $t('FOLLOW_UPS.FORM.NO_WHATSAPP_TEMPLATES') }}
              </p>
            </div>
            <select
              v-else
              :value="
                selectedWhatsappTemplate ? selectedWhatsappTemplate.name : ''
              "
              class="select-field w-full h-10 px-3 !bg-none bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all cursor-pointer"
              @change="onSelectWhatsappTemplate"
            >
              <option value="" disabled>
                {{ $t('FOLLOW_UPS.FORM.CHOOSE_WHATSAPP_TEMPLATE') }}
              </option>
              <option
                v-for="tpl in whatsappTemplates"
                :key="tpl.id || tpl.name"
                :value="tpl.name"
              >
                {{ tpl.name }}
              </option>
            </select>
            <WhatsAppTemplateParser
              v-if="selectedWhatsappTemplate"
              ref="templateParser"
              :template="selectedWhatsappTemplate"
              @validity-change="onTemplateValidity"
            >
              <template #actions />
            </WhatsAppTemplateParser>
          </div>

          <!-- Template Selector (when template mode) -->
          <div v-if="messageMode === 'template'" class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-11">
              {{ $t('FOLLOW_UPS.FORM.SELECT_TEMPLATE') }}
              <span class="text-n-ruby-10">*</span>
            </label>
            <div
              v-if="templates.length === 0"
              class="p-3 bg-n-amber-3 border border-n-amber rounded-lg"
            >
              <p class="text-sm text-n-amber-11">
                {{ $t('FOLLOW_UPS.FORM.NO_TEMPLATES') }}
              </p>
              <router-link
                :to="{ name: 'follow_up_automations' }"
                class="text-sm font-medium text-n-brand hover:underline mt-1 inline-block"
              >
                {{ $t('FOLLOW_UPS.FORM.CREATE_TEMPLATE') }}
              </router-link>
            </div>
            <select
              v-else
              v-model="selectedTemplateId"
              class="select-field w-full h-10 px-3 bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all cursor-pointer"
            >
              <option :value="null" disabled>
                {{ $t('FOLLOW_UPS.FORM.CHOOSE_TEMPLATE') }}
              </option>
              <option
                v-for="template in templates"
                :key="template.id"
                :value="template.id"
              >
                {{ template.name }}
              </option>
            </select>
          </div>

          <!-- Title Input -->
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-11">
              {{ $t('FOLLOW_UPS.FORM.TITLE_LABEL') }}
            </label>
            <input
              v-model="form.title"
              type="text"
              class="w-full px-3 py-2.5 bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all"
              :placeholder="$t('FOLLOW_UPS.FORM.TITLE_PLACEHOLDER')"
            />
          </div>

          <!-- Message Textarea (hidden in Meta template mode — the template IS the message) -->
          <div
            v-if="messageMode !== 'whatsapp_template'"
            class="flex flex-col gap-1.5"
          >
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-n-slate-11">
                {{ $t('FOLLOW_UPS.FORM.MESSAGE_LABEL') }}
                <span class="text-n-ruby-10">*</span>
              </label>
              <span
                v-if="messageMode === 'template' && selectedTemplate"
                class="text-xs text-n-slate-10"
              >
                {{ $t('FOLLOW_UPS.FORM.TEMPLATE_APPLIED') }}
              </span>
            </div>
            <textarea
              v-model="form.message"
              rows="4"
              class="w-full px-3 py-2.5 bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all resize-none"
              :placeholder="$t('FOLLOW_UPS.FORM.MESSAGE_PLACEHOLDER')"
            />
          </div>

          <!-- Two columns for Inbox and DateTime -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Inbox Selector -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">
                {{ $t('FOLLOW_UPS.FORM.INBOX_LABEL') }}
                <span class="text-n-ruby-10">*</span>
              </label>
              <select
                v-model="form.inboxId"
                class="select-field w-full h-10 px-3 bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all cursor-pointer"
              >
                <option value="" disabled>
                  {{ $t('FOLLOW_UPS.FORM.SELECT_INBOX') }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </div>

            <!-- Date & Time Picker -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-11">
                {{ $t('FOLLOW_UPS.FORM.SCHEDULE_TIME_LABEL') }}
                <span class="text-n-ruby-10">*</span>
              </label>
              <input
                v-model="form.scheduledAt"
                type="datetime-local"
                :min="minDateTime"
                class="datetime-field w-full h-10 px-3 bg-n-alpha-1 border border-n-weak rounded-lg text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent transition-all cursor-pointer"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div
            class="flex justify-end gap-3 pt-4 mt-2 border-t border-n-strong"
          >
            <NextButton v-if="isEditing" variant="faded" @click="cancelEdit">
              {{ $t('FOLLOW_UPS.FORM.CANCEL') }}
            </NextButton>
            <NextButton
              variant="solid"
              :disabled="!isFormValid"
              :is-loading="isSubmitting"
              @click="onSubmit"
            >
              <fluent-icon
                :icon="isEditing ? 'checkmark' : 'send-clock'"
                size="16"
                class="mr-1.5"
              />
              {{
                isEditing
                  ? $t('FOLLOW_UPS.FORM.UPDATE')
                  : $t('FOLLOW_UPS.FORM.SCHEDULE')
              }}
            </NextButton>
          </div>
        </div>

        <!-- Right Column: Follow-ups List -->
        <div
          class="flex flex-col lg:border-l border-t lg:border-t-0 border-n-weak pt-6 lg:pt-0 lg:pl-6 max-h-[50vh] lg:max-h-none"
        >
          <div
            class="flex items-center justify-between pb-3 mb-3 border-b border-n-strong"
          >
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ $t('FOLLOW_UPS.LIST.TITLE') }}
            </h4>
            <span
              class="px-2.5 py-1 text-xs font-medium bg-n-alpha-2 text-n-slate-11 rounded-full"
            >
              {{ followUps.length }}
              {{ $t('FOLLOW_UPS.LIST.ITEMS') }}
            </span>
          </div>

          <!-- Loading State -->
          <div
            v-if="isLoadingMessages"
            class="flex items-center justify-center flex-1 min-h-[200px]"
          >
            <Spinner size="small" />
          </div>

          <!-- Empty State -->
          <div
            v-else-if="followUps.length === 0"
            class="flex flex-col items-center justify-center flex-1 min-h-[200px] text-center py-8"
          >
            <div
              class="w-16 h-16 rounded-full bg-n-alpha-2 flex items-center justify-center mb-4"
            >
              <fluent-icon icon="calendar" size="32" class="text-n-slate-9" />
            </div>
            <p class="text-sm font-medium text-n-slate-11">
              {{ $t('FOLLOW_UPS.LIST.EMPTY') }}
            </p>
            <p class="text-xs text-n-slate-9 mt-1">
              {{ $t('FOLLOW_UPS.LIST.EMPTY_SUBTITLE') }}
            </p>
          </div>

          <!-- Messages List -->
          <div
            v-else
            class="flex flex-col gap-3 overflow-y-auto flex-1 pr-1 max-h-[40vh] lg:max-h-[45vh]"
          >
            <div
              v-for="message in sortedMessages"
              :key="message.id"
              class="p-3 bg-n-alpha-1 border border-n-weak rounded-xl hover:border-n-strong hover:shadow-sm transition-all duration-200"
            >
              <div class="flex items-start justify-between gap-2 mb-2">
                <div class="flex-1 min-w-0">
                  <h5 class="text-sm font-medium text-n-slate-12 truncate">
                    {{ message.title || $t('FOLLOW_UPS.LIST.NO_TITLE') }}
                  </h5>
                  <p
                    class="text-xs text-n-slate-10 mt-0.5 flex items-center gap-1"
                  >
                    <fluent-icon icon="send-clock" size="12" />
                    {{ formatDate(message.scheduled_at) }}
                  </p>
                </div>
                <span
                  class="px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap flex-shrink-0"
                  :class="getStatusClass(message.status)"
                >
                  {{ getStatusLabel(message.status) }}
                </span>
              </div>

              <p class="text-sm text-n-slate-11 line-clamp-2">
                {{ message.message }}
              </p>

              <div
                v-if="message.status === 'pending'"
                class="flex justify-end gap-2 pt-2 mt-2 border-t border-n-weak"
              >
                <NextButton
                  size="xs"
                  variant="ghost"
                  color="slate"
                  @click="editMessage(message)"
                >
                  <fluent-icon icon="edit" size="14" class="mr-1" />
                  {{ $t('FOLLOW_UPS.LIST.EDIT') }}
                </NextButton>
                <NextButton
                  size="xs"
                  variant="ghost"
                  color="ruby"
                  @click="confirmDelete(message)"
                >
                  <fluent-icon icon="delete" size="14" class="mr-1" />
                  {{ $t('FOLLOW_UPS.LIST.DELETE') }}
                </NextButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <woot-modal
        v-model:show="showDeleteConfirm"
        @close="() => (showDeleteConfirm = false)"
      >
        <woot-modal-header :header-title="$t('FOLLOW_UPS.DELETE.TITLE')" />
        <div class="p-6">
          <p class="text-sm text-n-slate-11">
            {{ $t('FOLLOW_UPS.DELETE.CONFIRM') }}
          </p>
          <div class="flex justify-end gap-3 mt-6">
            <NextButton variant="faded" @click="showDeleteConfirm = false">
              {{ $t('FOLLOW_UPS.DELETE.CANCEL') }}
            </NextButton>
            <NextButton
              variant="solid"
              color="ruby"
              :is-loading="isDeleting"
              @click="deleteMessage"
            >
              {{ $t('FOLLOW_UPS.DELETE.CONFIRM_BUTTON') }}
            </NextButton>
          </div>
        </div>
      </woot-modal>
    </div>
  </woot-modal>
</template>

<style scoped>
/* Wider modal */
.follow-up-modal :deep(.modal-container) {
  max-width: 900px;
  width: 95%;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Select field styling */
.select-field {
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
}

.select-field option {
  background: var(--n-slate-1);
  color: var(--n-slate-12);
  padding: 0.5rem;
}

/* Datetime field styling */
.datetime-field {
  color-scheme: light dark;
}

.datetime-field::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
}

.datetime-field::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

/* Custom scrollbar */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: var(--n-slate-6);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: var(--n-slate-7);
}
</style>
